// src/services/excelExport.js
import * as XLSX from 'xlsx';

// Стилі для Excel
const excelStyles = {
  // Заголовки
  header: {
    font: { bold: true, color: { rgb: 'FFFFFF' }, size: 12 },
    fill: { fgColor: { rgb: '2C3E50' } },
    alignment: { horizontal: 'center', vertical: 'center' },
    border: {
      top: { style: 'thin', color: { rgb: '1A252F' } },
      left: { style: 'thin', color: { rgb: '1A252F' } },
      bottom: { style: 'thin', color: { rgb: '1A252F' } },
      right: { style: 'thin', color: { rgb: '1A252F' } }
    }
  },
  // Підзаголовки
  subheader: {
    font: { bold: true, color: { rgb: '2C3E50' }, size: 11 },
    fill: { fgColor: { rgb: 'ECF0F1' } },
    border: {
      top: { style: 'thin', color: { rgb: 'BDC3C7' } },
      left: { style: 'thin', color: { rgb: 'BDC3C7' } },
      bottom: { style: 'thin', color: { rgb: 'BDC3C7' } },
      right: { style: 'thin', color: { rgb: 'BDC3C7' } }
    }
  },
  // Звичайні комірки
  cell: {
    font: { size: 10, color: { rgb: '2C3E50' } },
    border: {
      top: { style: 'thin', color: { rgb: 'ECF0F1' } },
      left: { style: 'thin', color: { rgb: 'ECF0F1' } },
      bottom: { style: 'thin', color: { rgb: 'ECF0F1' } },
      right: { style: 'thin', color: { rgb: 'ECF0F1' } }
    }
  },
  // Важливі значення
  highlight: {
    font: { bold: true, color: { rgb: 'E74C3C' }, size: 10 },
    border: {
      top: { style: 'thin', color: { rgb: 'ECF0F1' } },
      left: { style: 'thin', color: { rgb: 'ECF0F1' } },
      bottom: { style: 'thin', color: { rgb: 'ECF0F1' } },
      right: { style: 'thin', color: { rgb: 'ECF0F1' } }
    }
  },
  // Позитивні значення
  success: {
    font: { bold: true, color: { rgb: '27AE60' }, size: 10 },
    border: {
      top: { style: 'thin', color: { rgb: 'ECF0F1' } },
      left: { style: 'thin', color: { rgb: 'ECF0F1' } },
      bottom: { style: 'thin', color: { rgb: 'ECF0F1' } },
      right: { style: 'thin', color: { rgb: 'ECF0F1' } }
    }
  }
};

// Загальні утиліти
const excelUtils = {
  formatDate: (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('es-ES');
  },

  formatDateTime: (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('es-ES');
  },

  // Створення workbook з метаданими
  createWorkbook: (title) => {
    const workbook = XLSX.utils.book_new();
    
    // Додаємо метадані
    workbook.Props = {
      Title: title,
      Subject: 'LAINSA Production Manager',
      Author: 'LAINSA System',
      CreatedDate: new Date()
    };

    return workbook;
  },

  // Додавання стилів до аркуша
  applyStyles: (worksheet, ranges) => {
    if (!worksheet['!merges']) worksheet['!merges'] = [];
    if (!worksheet['!cols']) worksheet['!cols'] = [];
    if (!worksheet['!rows']) worksheet['!rows'] = [];

    // Додаємо об'єднання комірок
    if (ranges.merges) {
      worksheet['!merges'] = worksheet['!merges'].concat(ranges.merges);
    }

    // Налаштовуємо ширину стовпців
    if (ranges.cols) {
      ranges.cols.forEach((width, index) => {
        worksheet['!cols'][index] = { width };
      });
    }

    // Налаштовуємо висоту рядків
    if (ranges.rows) {
      ranges.rows.forEach((height, index) => {
        worksheet['!rows'][index] = { hpt: height };
      });
    }

    return worksheet;
  }
};

// Експорт одного звіту оператора
export const exportOperatorReportToExcel = (report) => {
  const workbook = excelUtils.createWorkbook(`Reporte_Turno_${report.fecha}_${report.turno}`);
  
  // Основний аркуш
  const worksheetData = [];
  
  // Заголовок
  worksheetData.push(
    ['LAINSA - REPORTE DE TURNO'],
    [`Fecha: ${excelUtils.formatDate(report.fecha)} | Turno: ${report.turno || report.shift} | Operadores: ${report.operators?.join(', ') || 'N/A'}`],
    [''] // Пустий рядок
  );

  // Виробництво тунелю
  worksheetData.push(['PRODUCCIÓN TÚNEL']);
  worksheetData.push(['Código', 'Descripción', 'Peso (kg)']);
  
  if (report.tunel) {
    Object.entries(report.tunel).forEach(([key, value]) => {
      if (value && value > 0) {
        const programName = getProgramName(key);
        worksheetData.push([key.toUpperCase(), programName, value]);
      }
    });
  }
  
  worksheetData.push(['']);

  // Відходи тунелю
  worksheetData.push(['RECHAZOS TÚNEL']);
  worksheetData.push(['Código', 'Descripción', 'Peso (kg)']);
  
  if (report.rech_tunel) {
    Object.entries(report.rech_tunel).forEach(([key, value]) => {
      if (value && value > 0) {
        const programName = getProgramName(key);
        worksheetData.push([key.toUpperCase(), programName, value]);
      }
    });
  }
  
  worksheetData.push(['']);

  // Лічильники
  worksheetData.push(['LECTURAS DE CONTADORES']);
  worksheetData.push(['Contador', 'Valor']);
  
  if (report.cont) {
    Object.entries(report.cont).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        const meterName = getMeterName(key);
        worksheetData.push([meterName, value]);
      }
    });
  }
  
  worksheetData.push(['']);

  // Цикли пральних машин
  if (report.lav_ciclos && Object.keys(report.lav_ciclos).length > 0) {
    worksheetData.push(['CICLOS DE LAVADORAS']);
    worksheetData.push(['Lavadora', 'Ciclos']);
    
    Object.entries(report.lav_ciclos).forEach(([key, value]) => {
      if (value && value > 0) {
        worksheetData.push([key, value]);
      }
    });
    
    worksheetData.push(['']);
  }

  // Вага по клієнтах
  if (report.pesoCliente && report.pesoCliente.length > 0) {
    worksheetData.push(['PESO POR CLIENTE']);
    worksheetData.push(['Cliente', 'Programa', 'Peso (kg)']);
    
    report.pesoCliente.forEach(item => {
      if (item.client && item.weight) {
        worksheetData.push([item.client, item.program || 'N/A', item.weight]);
      }
    });
    
    worksheetData.push(['']);
  }

  // Спостереження
  if (report.observaciones) {
    worksheetData.push(['OBSERVACIONES']);
    worksheetData.push([report.observaciones]);
  }

  // Створюємо аркуш
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  
  // Застосовуємо стилі
  const styledWorksheet = excelUtils.applyStyles(worksheet, {
    cols: [15, 40, 15, 20, 15, 15],
    rows: [25, 20, 20, 15, 15, 15],
    merges: [
      { s: { r: 0, c: 0 }, e: { r: 0, c: 5 } }, // Заголовок
      { s: { r: 1, c: 0 }, e: { r: 1, c: 5 } } // Підзаголовок
    ]
  });

  // Додаємо аркуш до workbook
  XLSX.utils.book_append_sheet(workbook, styledWorksheet, 'Reporte Turno');

  // Генеруємо файл
  const fileName = `Reporte_Turno_${report.fecha}_${report.turno || 'turno'}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

// Експорт списку звітів
export const exportOperatorReportsToExcel = (reports, filters = {}) => {
  const workbook = excelUtils.createWorkbook('Reportes_Operador');
  
  // Основний аркуш зі зведеною інформацією
  const summaryData = [
    ['LAINSA - REPORTES DE OPERADOR'],
    [`Generado: ${excelUtils.formatDateTime(new Date())} | Total reportes: ${reports.length}`],
    ['']
  ];

  // Інформація про фільтри
  const filterInfo = [];
  if (filters.dateFrom) filterInfo.push(`Desde: ${excelUtils.formatDate(filters.dateFrom)}`);
  if (filters.dateTo) filterInfo.push(`Hasta: ${excelUtils.formatDate(filters.dateTo)}`);
  if (filters.shift && filters.shift !== 'all') filterInfo.push(`Turno: ${filters.shift}`);
  
  if (filterInfo.length > 0) {
    summaryData.push([`Filtros: ${filterInfo.join(' | ')}`]);
  }
  
  summaryData.push(['']);

  // Заголовки таблиці
  summaryData.push([
    'Fecha', 
    'Turno', 
    'Operadores', 
    'Producción Total (kg)', 
    'Rechazos Total (kg)', 
    'Eficiencia (%)',
    'Contadores Agua',
    'Contadores Propano'
  ]);

  // Дані звітів
  reports.forEach(report => {
    const production = report.tunel?.total || 0;
    const rejection = report.rech_tunel?.total || 0;
    const efficiency = production > 0 ? ((production - rejection) / production * 100).toFixed(1) : 'N/A';
    
    const waterMeters = countMetersByType(report.cont, 'water');
    const propaneMeters = countMetersByType(report.cont, 'propane');

    summaryData.push([
      excelUtils.formatDate(report.fecha),
      report.turno || report.shift || 'N/A',
      report.operators?.join(', ') || 'N/A',
      production,
      rejection,
      efficiency,
      waterMeters,
      propaneMeters
    ]);
  });

  // Створюємо аркуш зі зведенням
  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);
  const styledSummarySheet = excelUtils.applyStyles(summarySheet, {
    cols: [12, 10, 25, 15, 15, 12, 15, 15],
    rows: [25, 20, 20, 15]
  });

  XLSX.utils.book_append_sheet(workbook, styledSummarySheet, 'Resumen');

  // Детальний аркуш з виробництвом по програмах
  if (reports.length > 0) {
    createDetailedProductionSheet(workbook, reports);
  }

  // Аркуш з лічильниками
  if (reports.some(report => report.cont)) {
    createMetersSheet(workbook, reports);
  }

  // Генеруємо файл
  const fileName = `Reportes_Operador_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

// Допоміжні функції
function getProgramName(key) {
  const programs = {
    pro1: 'Pro.1 - Sábanas',
    pro2: 'Pro.2 - Sábana nueva', 
    pro3: 'Pro.3 - Toalla nueva',
    pro4: 'Pro.4 - Mantelería',
    pro6: 'Pro.6 - Toallas espigas/rayas',
    pro8: 'Pro.8 - Toallas color',
    pro9: 'Pro.9 - Nórdicos',
    pro10: 'Pro.10 - Premium',
    pro14: 'Pro.14 - Albornoces',
    total: 'TOTAL'
  };
  return programs[key] || key;
}

function getMeterName(key) {
  const meters = {
    agua_tunel: 'Agua Túnel',
    agua_pozo: 'Agua Pozo',
    agua_lavadoras: 'Agua Lavadoras',
    agua_calderas: 'Agua Calderas',
    propano_deposito: 'Propano Depósito',
    propano_caldera: 'Propano Caldera',
    propano_saba: 'Propano Saba',
    propano_medio: 'Propano Medio',
    propano_hibri: 'Propano Híbrido',
    propano_secadora: 'Propano Secadora'
  };
  return meters[key] || key;
}

function countMetersByType(contadores, type) {
  if (!contadores) return 0;
  
  const waterKeys = ['agua_tunel', 'agua_pozo', 'agua_lavadoras', 'agua_calderas'];
  const propaneKeys = ['propano_deposito', 'propano_caldera', 'propano_saba', 'propano_medio', 'propano_hibri', 'propano_secadora'];
  
  const keysToCheck = type === 'water' ? waterKeys : propaneKeys;
  
  return keysToCheck.filter(key => 
    contadores[key] !== null && 
    contadores[key] !== undefined && 
    contadores[key] !== '' && 
    contadores[key] !== 0
  ).length;
}

function createDetailedProductionSheet(workbook, reports) {
  const productionData = [
    ['DETALLE DE PRODUCCIÓN POR PROGRAMA'],
    [''],
    ['Fecha', 'Turno', 'Programa', 'Descripción', 'Producción (kg)', 'Rechazos (kg)']
  ];

  reports.forEach(report => {
    // Виробництво
    if (report.tunel) {
      Object.entries(report.tunel).forEach(([key, value]) => {
        if (value && value > 0 && key !== 'total') {
          productionData.push([
            excelUtils.formatDate(report.fecha),
            report.turno || report.shift || 'N/A',
            key.toUpperCase(),
            getProgramName(key),
            value,
            report.rech_tunel?.[key] || 0
          ]);
        }
      });
    }
  });

  const productionSheet = XLSX.utils.aoa_to_sheet(productionData);
  const styledProductionSheet = excelUtils.applyStyles(productionSheet, {
    cols: [12, 10, 12, 30, 15, 15]
  });

  XLSX.utils.book_append_sheet(workbook, styledProductionSheet, 'Producción Detallada');
}

function createMetersSheet(workbook, reports) {
  const metersData = [
    ['LECTURAS DE CONTADORES'],
    [''],
    ['Fecha', 'Turno', 'Contador', 'Tipo', 'Valor']
  ];

  reports.forEach(report => {
    if (report.cont) {
      Object.entries(report.cont).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          const meterType = key.includes('agua') ? 'Agua' : key.includes('propano') ? 'Propano' : 'Otro';
          metersData.push([
            excelUtils.formatDate(report.fecha),
            report.turno || report.shift || 'N/A',
            getMeterName(key),
            meterType,
            value
          ]);
        }
      });
    }
  });

  const metersSheet = XLSX.utils.aoa_to_sheet(metersData);
  const styledMetersSheet = excelUtils.applyStyles(metersSheet, {
    cols: [12, 10, 25, 10, 15]
  });

  XLSX.utils.book_append_sheet(workbook, styledMetersSheet, 'Contadores');
}

// Експорт для MeterReports
export const exportMeterReportsToExcel = (reports, filters = {}) => {
  const workbook = excelUtils.createWorkbook('Reportes_Contadores');
  
  const data = [
    ['LAINSA - REPORTES DE CONTADORES'],
    [`Generado: ${excelUtils.formatDateTime(new Date())} | Total registros: ${reports.length}`],
    ['']
  ];

  // Інформація про фільтри
  const filterInfo = [];
  if (filters.dateFrom) filterInfo.push(`Desde: ${excelUtils.formatDate(filters.dateFrom)}`);
  if (filters.dateTo) filterInfo.push(`Hasta: ${excelUtils.formatDate(filters.dateTo)}`);
  if (filters.type && filters.type !== 'all') filterInfo.push(`Tipo: ${filters.type}`);
  
  if (filterInfo.length > 0) {
    data.push([`Filtros: ${filterInfo.join(' | ')}`]);
  }
  
  data.push(['']);

  // Заголовки
  data.push([
    'ID', 
    'Fecha', 
    'Tipo',
    'Contadores',
    'Valores Principales'
  ]);

  // Дані
  reports.forEach(report => {
    const meterType = getMeterTypeFromContadores(report.cont);
    const metersList = report.cont ? Object.keys(report.cont).slice(0, 3).join(', ') + (Object.keys(report.cont).length > 3 ? '...' : '') : 'Sin datos';
    const mainValues = report.cont ? Object.entries(report.cont)
      .slice(0, 2)
      .map(([key, value]) => `${getMeterName(key)}: ${value}`)
      .join(', ') : 'Sin valores';

    data.push([
      report.id?.substring(0, 15) + '...',
      excelUtils.formatDate(report.fecha || report.date),
      meterType,
      metersList,
      mainValues
    ]);
  });

  const worksheet = XLSX.utils.aoa_to_sheet(data);
  const styledWorksheet = excelUtils.applyStyles(worksheet, {
    cols: [20, 12, 12, 30, 30]
  });

  XLSX.utils.book_append_sheet(workbook, styledWorksheet, 'Contadores');

  const fileName = `Reportes_Contadores_${new Date().toISOString().split('T')[0]}.xlsx`;
  XLSX.writeFile(workbook, fileName);
};

function getMeterTypeFromContadores(contadores) {
  if (!contadores) return 'Otro';
  
  const hasWater = Object.keys(contadores).some(key => key.includes('agua'));
  const hasPropane = Object.keys(contadores).some(key => key.includes('propano'));
  
  if (hasWater && hasPropane) return 'Mixto';
  if (hasWater) return 'Agua';
  if (hasPropane) return 'Propano';
  return 'Otro';
}

export default {
  exportOperatorReportToExcel,
  exportOperatorReportsToExcel,
  exportMeterReportsToExcel
};


